import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, MenuItem, TextField, Typography, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { CardWrapper, SectionTitle, formatBRL } from '../../../UI';
import TableParcelasPassoTres from './TableParcelasPassoTres';
import DialogAlterarParcela from './DialogAlterarParcela';
import {
  aplicarMascaraTelefone, contarPalavras, makePayloadGerarAcordo, vencimentoParaIso,
  resolverResultadoGeracao, MENSAGEM_ERRO_GERAR,
} from './passoTresController';
import { ITEMS_ANALISE_ACORDO } from '../PassoDois/passoDoisConstants';
import {
  gerarAcordoPasso3, getTelefoneDefault,
} from '../../../../../services/core/processos/processosAcordosService';
import { getParametrosInterno } from '../../../../../services/core/parametro/parametro.services';
import { buscarOcorrenciasGrupoElfa } from '../../../../../services/core/processos/grupoElfaOcorrenciasService';
import {
  findTiposFormaPagamentoAction, findTiposTelefoneAction, findUsuariosPermitemSimulacaoAction,
  findBancosAction,
} from '../../../../../store/Global/LoadSelectors/loadSelectors.saga';

const MENSAGEM_ERRO_GRUPO_ELFA = 'Não foi possível verificar o status Elfa do processo. O fluxo de acordo seguirá como padrão.';

/**
 * Passo 3 de Novo Acordo (telefone, parcelas, aprovação, geração) — port de
 * `NovoAcordoPassoTres.jsx` real.
 *
 * Esta é a primeira etapa do fluxo que MUTA dado real (`gerarAcordoPasso3`) —
 * todo `idProcessoRef` guard é ainda mais crítico aqui do que nas etapas de
 * simulação anteriores.
 */
export default function NovoAcordoPassoTres({
  idProcesso, dadosResumo, parcelas, setParcelas, dadosPadraoForm, dataFinalPasso3,
  onVoltar, onFechar, onAcordoGerado, isContraProposta, isForaPadrao,
}) {
  const dispatch = useDispatch();
  const formasPagamentoOptions = useSelector(states => states.selectors.tiposFormaPagamento.selector);
  const tiposTelefoneOptions = useSelector(states => states.selectors.tiposTelefone.selector);
  const usuariosPermitemProposta = useSelector(states => states.selectors.usuariosPermitemSimulacao.selector);
  const bancosOptions = useSelector(states => states.selectors.bancos.selector);

  const [telefone, setTelefone] = useState({ numero: '', ddd: '', tipoTelefone: 'CELULAR' });
  const [idUsuarioAprovador, setIdUsuarioAprovador] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [ordenacaoAscendente, setOrdenacaoAscendente] = useState(true);
  const [parcelaEmEdicao, setParcelaEmEdicao] = useState(null);
  const [parametrosInternosAbe, setParametrosInternosAbe] = useState({});
  const [ehGrupoElfa, setEhGrupoElfa] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [avisoNaoBloqueante, setAvisoNaoBloqueante] = useState('');
  const [erroSomatoria, setErroSomatoria] = useState('');

  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  useEffect(() => {
    dispatch(findTiposFormaPagamentoAction());
    dispatch(findTiposTelefoneAction());
    dispatch(findUsuariosPermitemSimulacaoAction());
    dispatch(findBancosAction());
  }, [dispatch]);

  useEffect(() => {
    getParametrosInterno()
      .then(({ data }) => setParametrosInternosAbe(data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setCarregando(false);
    setErro('');
    setAviso('');
    setAvisoNaoBloqueante('');
    setErroSomatoria('');
    setTelefone({ numero: '', ddd: '', tipoTelefone: 'CELULAR' });
    setIdUsuarioAprovador('');
    setJustificativa('');

    const alvo = idProcesso;
    getTelefoneDefault(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setTelefone({ numero: data?.numero || '', ddd: data?.ddd || '', tipoTelefone: data?.idTipoTelefone || 'CELULAR' });
        }
      })
      .catch(() => {});

    buscarOcorrenciasGrupoElfa(alvo)
      .then(({ data }) => {
        if (idProcessoRef.current === alvo) {
          setEhGrupoElfa(data?.ehGrupoElfa === true);
        }
      })
      .catch(() => {
        if (idProcessoRef.current === alvo) {
          setEhGrupoElfa(false);
          setAvisoNaoBloqueante(MENSAGEM_ERRO_GRUPO_ELFA);
        }
      });
  }, [idProcesso]);

  const onClickColunaVencimento = useCallback(() => {
    setOrdenacaoAscendente((atual) => {
      const proximaOrdem = !atual;
      const ordenadas = [...parcelas].sort((a, b) => {
        const dataA = new Date(vencimentoParaIso(a.vencimento));
        const dataB = new Date(vencimentoParaIso(b.vencimento));
        return proximaOrdem ? dataA - dataB : dataB - dataA;
      });
      setParcelas(ordenadas.map((parcela, indice) => ({
        ...parcela,
        nParcela: proximaOrdem ? indice + 1 : ordenadas.length - indice,
      })));
      return proximaOrdem;
    });
  }, [parcelas, setParcelas]);

  const onSalvarParcelaAlterada = (novasParcelas) => {
    setParcelas(novasParcelas);
    setParcelaEmEdicao(null);
  };

  const totalParcelas = parcelas.reduce((total, parcela) => total + parcela.valor, 0);
  const jsonErros = dadosResumo?.jsonErros;
  const semRestricoes = Boolean(jsonErros)
    && ITEMS_ANALISE_ACORDO.every(item => jsonErros[item.colorProp] !== 'ERRO' && jsonErros[item.colorProp] !== 'ALERTA');

  const precisaAprovador = Boolean(jsonErros) && !semRestricoes && !ehGrupoElfa;

  const justificativaValida = contarPalavras(justificativa) >= 5;
  const aprovadorValido = idUsuarioAprovador !== '' && idUsuarioAprovador !== 0;
  const validacoesPropostaFalharam = telefone.numero === '' || !aprovadorValido || !justificativaValida;

  const desabilitarGerarSimulacao = ehGrupoElfa
    || (semRestricoes && telefone.numero === '')
    || (!semRestricoes && validacoesPropostaFalharam && !isContraProposta);

  const desabilitarGerarAcordo = !semRestricoes || telefone.numero === '' || carregando;

  const rotuloBotaoSimulacao = !semRestricoes && !isContraProposta ? 'Gerar Proposta' : 'Gerar Simulação';

  const onChangeTelefone = campo => (event) => {
    const { value } = event.target;
    setTelefone(atual => ({ ...atual, [campo]: campo === 'numero' ? aplicarMascaraTelefone(value) : value }));
  };

  const submeterGeracao = useCallback(async (tipo) => {
    const alvo = idProcesso;
    try {
      setCarregando(true);
      const payload = makePayloadGerarAcordo({
        idProcesso: alvo,
        dadosResumo,
        parcelas,
        dadosPadraoForm,
        tipo,
        formasPagamentoOptions,
        idUsuarioAprovador,
        justificativa,
        telefone,
      });
      const { data } = await gerarAcordoPasso3(payload);
      if (idProcessoRef.current !== alvo) {
        return;
      }

      const resultado = resolverResultadoGeracao(data, tipo);
      switch (resultado.acao) {
        case 'FECHAR':
          onFechar();
          if (onAcordoGerado) {
            onAcordoGerado();
          }
          break;
        case 'AVISO_E_FECHAR':
          // Não fecha aqui — fechar junto com `setAviso` desmontaria o painel
          // antes do Snackbar pintar, escondendo a mensagem do usuário. O
          // fechamento acontece no `onClose` do Snackbar de aviso, abaixo.
          // `onAcordoGerado` já dispara agora, e não junto do fechamento: a
          // mutação no backend já ocorreu neste ponto, então os cards de
          // ControleProcessosPage.jsx já estão desatualizados independente de
          // quando o usuário dispensar o Snackbar.
          setAviso(resultado.mensagem);
          if (onAcordoGerado) {
            onAcordoGerado();
          }
          break;
        case 'ERRO_SOMATORIA':
          setErroSomatoria(resultado.mensagem);
          break;
        default:
          setErro(resultado.mensagem);
          break;
      }
    } catch {
      if (idProcessoRef.current === alvo) {
        setErro(MENSAGEM_ERRO_GERAR);
      }
    } finally {
      if (idProcessoRef.current === alvo) {
        setCarregando(false);
      }
    }
  }, [idProcesso, dadosResumo, parcelas, dadosPadraoForm, formasPagamentoOptions, idUsuarioAprovador, justificativa, telefone, onFechar, onAcordoGerado]);

  const onClickGerarSimulacao = () => {
    // `ehGrupoElfa` força o tipo seguro ('S') mesmo que `semRestricoes` seja
    // false — replica o ponto 2 ("Dispatch de tipo") do padrão de defesa em
    // profundidade documentado em `src/pages/InspecionarProcesso/Acordos/CLAUDE.md`
    // ("Bloqueio de tipo de transação por flag booleana"): o disable do botão
    // (ponto 3) já cobre o clique direto, mas o dispatch de tipo não pode
    // depender só disso.
    submeterGeracao(ehGrupoElfa || semRestricoes ? 'S' : 'SP');
  };

  const onClickGerarAcordo = () => {
    submeterGeracao('A');
  };

  const onFecharComAviso = () => {
    setAviso('');
    onFechar();
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Confira os valores, formas de pagamentos e quantidade de parcelas para o Acordo. Confirme o Acordo/Simulação
        ou refaça os passos anteriores.
      </Typography>

      <CardWrapper sx={{ mb: 2 }}>
        <TableParcelasPassoTres
          parcelas={parcelas}
          formasPagamentoOptions={formasPagamentoOptions}
          formaPagamentoPasso2={dadosPadraoForm?.idTipoFormaPagto}
          ordenacaoAscendente={ordenacaoAscendente}
          onClickColunaVencimento={onClickColunaVencimento}
          onClickAlterar={setParcelaEmEdicao}
        />
      </CardWrapper>

      <Box sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2,
      }}
      >
        <CardWrapper>
          <SectionTitle>Telefone de contato do Devedor</SectionTitle>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Tipo Telefone"
              value={telefone.tipoTelefone}
              onChange={onChangeTelefone('tipoTelefone')}
              size="small"
              sx={{ width: 160 }}
            >
              {tiposTelefoneOptions.map(opcao => (
                <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="DDD"
              value={telefone.ddd}
              onChange={onChangeTelefone('ddd')}
              size="small"
              inputProps={{ maxLength: 2 }}
              sx={{ width: 80 }}
            />
            <TextField
              label="Telefone"
              value={telefone.numero}
              onChange={onChangeTelefone('numero')}
              size="small"
              inputProps={{ maxLength: 14 }}
              sx={{ flex: 1 }}
            />
          </Box>
        </CardWrapper>
        <CardWrapper>
          <SectionTitle>Valores Acordados</SectionTitle>
          <Typography variant="body2">
            Quantidade de parcelas:
            {' '}
            {dadosPadraoForm?.quantidadeParcelas}
          </Typography>
          <Typography variant="body2">
            Valor do acordo:
            {' '}
            {dadosResumo?.valorTotal == null ? '—' : formatBRL(dadosResumo.valorTotal)}
          </Typography>
          <Typography variant="caption" sx={{ color: totalParcelas === dadosResumo?.valorTotal ? 'text.secondary' : 'error.main' }}>
            Soma das parcelas:
            {' '}
            {formatBRL(totalParcelas)}
          </Typography>
        </CardWrapper>
      </Box>

      {precisaAprovador && (
        <CardWrapper sx={{ mb: 2 }}>
          <SectionTitle>Aprovação da Proposta</SectionTitle>
          <TextField
            select
            label="Selecione o usuário para aprovar a Proposta *"
            value={idUsuarioAprovador}
            onChange={e => setIdUsuarioAprovador(e.target.value)}
            size="small"
            sx={{ width: '50%', mb: 2 }}
          >
            {usuariosPermitemProposta.map(opcao => (
              <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Justificativa *"
            value={justificativa}
            onChange={e => setJustificativa(e.target.value)}
            fullWidth
            multiline
            rows={4}
            helperText="Insira aqui a justificativa para a aprovação da Proposta (mínimo 5 palavras)"
          />
        </CardWrapper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button onClick={onVoltar} disabled={carregando}>Voltar</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onClickGerarSimulacao}
          disabled={desabilitarGerarSimulacao || carregando}
        >
          {rotuloBotaoSimulacao}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onClickGerarAcordo}
          disabled={desabilitarGerarAcordo}
        >
          Gerar Acordo
        </Button>
      </Box>

      <DialogAlterarParcela
        open={Boolean(parcelaEmEdicao)}
        parcela={parcelaEmEdicao || {}}
        parcelas={parcelas}
        dadosPadraoForm={dadosPadraoForm}
        dadosResumo={dadosResumo}
        dataFinalPasso3={dataFinalPasso3}
        prazoMaximoPrimeiroVencimento={parametrosInternosAbe?.prazoMaximoPrimeiroVencimento}
        formaPagamentoPasso2={dadosPadraoForm?.idTipoFormaPagto}
        formasPagamentoOptions={formasPagamentoOptions}
        bancosOptions={bancosOptions}
        onClose={() => setParcelaEmEdicao(null)}
        onSalvar={onSalvarParcelaAlterada}
      />

      <Dialog open={Boolean(erroSomatoria)} onClose={() => setErroSomatoria('')} maxWidth="sm" fullWidth hideBackdrop>
        <DialogTitle>Erro ao gerar Acordo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{erroSomatoria}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErroSomatoria('')}>OK, ENTENDI</Button>
        </DialogActions>
      </Dialog>

      {/*
        Dialog de contraproposta — inerte nesta entrega: `isContraProposta`/`isForaPadrao`
        sempre chegam `false` (nenhum caller do prototipo os define), pois dependem de uma
        feature fora de escopo (analise de contraproposta). Portado por decisao explicita
        do usuario, para paridade estrutural com `NovoAcordoPassoTres.jsx` real.
      */}
      <Dialog open={Boolean(isContraProposta && isForaPadrao)} maxWidth="sm" fullWidth hideBackdrop>
        <DialogTitle>Valores fora do padrão</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Os valores informados estão fora do padrão praticado (contraproposta). Deseja confirmar a geração mesmo assim?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onVoltar}>Cancelar</Button>
          <Button variant="contained" onClick={onClickGerarAcordo}>Confirmar mesmo assim</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(erro)} autoHideDuration={4000} onClose={() => setErro('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>{erro}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(aviso)} autoHideDuration={4000} onClose={onFecharComAviso} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="warning" variant="filled" onClose={onFecharComAviso}>{aviso}</Alert>
      </Snackbar>
      {/*
        Snackbar separado do `aviso` acima de propósito: este é usado só pelo
        catch de `buscarOcorrenciasGrupoElfa` (falha de rede transitória, não
        bloqueante — "o fluxo de acordo seguirá como padrão"). Reaproveitar o
        mesmo state/Snackbar do `aviso` (que fecha o painel via `onFecharComAviso`)
        fecharia o painel inteiro sozinho após uma falha de rede não relacionada
        a nenhuma ação do usuário — achado em code review.
      */}
      <Snackbar open={Boolean(avisoNaoBloqueante)} autoHideDuration={4000} onClose={() => setAvisoNaoBloqueante('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="warning" variant="filled" onClose={() => setAvisoNaoBloqueante('')}>{avisoNaoBloqueante}</Alert>
      </Snackbar>
    </Box>
  );
}

NovoAcordoPassoTres.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dadosResumo: PropTypes.object,
  parcelas: PropTypes.array,
  setParcelas: PropTypes.func.isRequired,
  dadosPadraoForm: PropTypes.object,
  dataFinalPasso3: PropTypes.string,
  onVoltar: PropTypes.func.isRequired,
  onFechar: PropTypes.func.isRequired,
  onAcordoGerado: PropTypes.func,
  isContraProposta: PropTypes.bool,
  isForaPadrao: PropTypes.bool,
};

NovoAcordoPassoTres.defaultProps = {
  idProcesso: null,
  dadosResumo: {},
  parcelas: [],
  dadosPadraoForm: {},
  dataFinalPasso3: '',
  onAcordoGerado: null,
  isContraProposta: false,
  isForaPadrao: false,
};
